import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { imgSrc } from '../lib/imgSrc';
import { EVENT_TYPE_META } from '../lib/historyMeta';
import { getPriorityLabel } from '../lib/priority';
import { SerializedWorkSummary } from './SerializedWorkSummary';
import { WorkOrderFindingsSummary } from './WorkOrderFindingsSummary';
import { WorkOrderPrintButton } from './WorkOrderPrintButton';

/**
 * Read-only summary popover for a single maintenance-history event.
 *
 * Fed by the calendar side panel (and, eventually, the parts-tracker
 * drilldown). Renders the full log body inline so viewers don't need
 * to navigate to the equipment detail page just to read what happened.
 *
 * Props:
 *   open        — bool
 *   onClose     — fn
 *   event       — event object from /api/calendar-events or /api/maintenance-history
 *                 (must include description/resolution/parts_used/notes/actor_name
 *                 when available; falls back gracefully)
 */
const Field = ({ label, value, mono = false }) => {
  if (value == null || value === '') return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{label}</div>
      <div className={`text-sm text-slate-900 mt-0.5 whitespace-pre-wrap ${mono ? 'font-mono' : ''}`}>
        {value}
      </div>
    </div>
  );
};

const LogSummaryPopover = ({ open, onClose, event }) => {
  const navigate = useNavigate();
  const [imgIdx, setImgIdx] = useState(null);

  if (!event) return null;
  const meta = EVENT_TYPE_META[event.event_type] || EVENT_TYPE_META.maintenance_log;
  const images = event.images || [];

  const workDateStr = event.date || event.event_date || '';
  const workDatePretty = (() => {
    if (!workDateStr) return '';
    try {
      const d = new Date(workDateStr);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleString(undefined, {
          weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
      }
    } catch { /* noop */ }
    return workDateStr;
  })();

  const canOpenEquipment = Boolean(event.machinery_id);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="log-summary-popover">
        <DialogHeader>
          <DialogTitle className="flex items-start gap-2 flex-wrap">
            <Badge className={`${meta.chipClass} text-white`}>{meta.label}</Badge>
            {event.wo_number && (
              <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                #{event.wo_number}
              </span>
            )}
            <span className="text-lg leading-tight">{event.title || 'Maintenance event'}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-slate-800">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Equipment" value={event.equipment_name} />
            <Field label="Date" value={workDatePretty} />
            {event.hours_spent != null && event.hours_spent !== '' && (
              <Field label="Hours spent" value={`${event.hours_spent} h`} />
            )}
            {event.actor_name && <Field label="Technician" value={event.actor_name} />}
            {event.priority && <Field label="Priority" value={getPriorityLabel(event.priority)} />}
            {event.status && <Field label="Status" value={event.status} />}
          </div>

          <Field label="Work description / Issue" value={event.description} />
          <Field label="Resolution" value={event.resolution} />
          <Field label="Parts used" value={event.parts_used} mono />
          <WorkOrderFindingsSummary findings={event.findings} followUps={event.follow_ups} machineryId={event.machinery_id} testId={`popover-findings-${event.id}`} />
          <SerializedWorkSummary records={event.serialized_work} testId={`popover-serial-work-${event.id}`} />
          <Field label="Notes" value={event.notes} />

          {images.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-2">
                Images ({images.length})
              </div>
              <div className="grid grid-cols-3 gap-2">
                {images.map((im, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setImgIdx(i)}
                    className="focus:outline-none focus:ring-2 focus:ring-slate-400 rounded overflow-hidden"
                    data-testid={`log-summary-image-${i}`}
                  >
                    <img
                      src={imgSrc(im, { thumb: true })}
                      alt={`Attachment ${i + 1}`}
                      className="h-24 w-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t mt-4">
          {['completed_work_order', 'open_work_order'].includes(event.event_type) && event.raw_id && <WorkOrderPrintButton workOrderId={event.raw_id} testId={`calendar-print-wo-btn-${event.raw_id}`} />}
          <Button variant="outline" onClick={onClose} data-testid="log-summary-close-btn">
            Close
          </Button>
          {canOpenEquipment && (
            <Button
              onClick={() => {
                onClose();
                navigate(`/machinery/${event.machinery_id}`);
              }}
              data-testid="log-summary-open-equipment-btn"
            >
              Open equipment page →
            </Button>
          )}
        </div>

        {imgIdx != null && images[imgIdx] && (
          <div
            className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4"
            onClick={() => setImgIdx(null)}
            data-testid="log-summary-lightbox"
          >
            <img
              src={imgSrc(images[imgIdx])}
              alt={`Attachment ${imgIdx + 1}`}
              className="max-h-[90vh] max-w-[90vw] object-contain"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LogSummaryPopover;
